import { challengeCrudConfig, type Challenge } from '@/config/objects/challenges';
import { CrudListView, type CrudConfig, type CrudListHeaderAction } from '@startupquickstart/frontend';
import { useCallback, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

interface ChallengesListProps {
  /**
   * Optional custom config (with modified endpoints, actions, etc.)
   */
  config?: CrudConfig<Challenge>;
  /**
   * Optional default where clause for filtering
   */
  defaultWhere?: Record<string, unknown>;
  /**
   * Optional custom onItemClick handler
   */
  onItemClick?: (challenge: Challenge) => void;
  /**
   * Custom class name for the container
   */
  className?: string;
  /**
   * Base URL for navigation, defaults to /challenges
   */
  baseUrl?: string;
  /**
   * Custom header actions (displayed in the header alongside Add/Create buttons)
   */
  headerActions?: CrudListHeaderAction[];
  /**
   * Breadcrumbs for navigation
   */
  breadcrumbs?: Array<{ label: string; href?: string; current?: boolean }>;
  /**
   * Arbitrary content rendered in the list header's right-hand area (e.g. a
   * List/Map toggle), before the Add/Create buttons.
   */
  headerRight?: ReactNode;
  /**
   * When set, replaces the table with this content (e.g. a map view) while
   * keeping the breadcrumbs and header identical.
   */
  contentOverride?: ReactNode;
}

/**
 * Reusable challenges list component
 * Pass a custom config with modified actions to customize behavior
 */
export function ChallengesList({
  config,
  baseUrl = '/challenges',
  defaultWhere,
  onItemClick,
  className,
  headerActions,
  breadcrumbs,
  headerRight,
  contentOverride,
}: ChallengesListProps) {
  const navigate = useNavigate();

  const _onItemClick = useCallback(
    (challenge: Challenge) => {
      if (onItemClick) {
        onItemClick(challenge);
      } else {
        navigate(`/challenges/${challenge.id}`);
      }
    },
    [navigate, onItemClick],
  );

  return (
    <div className={className}>
      <CrudListView
        config={config || challengeCrudConfig}
        baseUrl={baseUrl}
        onItemClick={_onItemClick}
        defaultWhere={defaultWhere}
        headerActions={headerActions}
        headerRight={headerRight}
        contentOverride={contentOverride}
        breadcrumbs={breadcrumbs}
        getValueFromItem={(challenge: Challenge) => challenge.id}
        getLabelFromItem={(challenge: Challenge) => challenge.name}
      />
    </div>
  );
}
